import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiFetch } from '../../lib/api';
import { siteLabel } from '../../lib/sites';
import { formatSourceDate } from '../../lib/formatting';
import type { ConflictCardResponse } from '../brief/ConflictResolveModal';
import {
  IDENTITY_CLAIM_NOTIFICATION_TYPE,
  IdentityClaimPayloadSchema,
  type IdentityClaimPayload,
} from '@terum/contracts';

interface NotificationItem {
  id: string;
  type: string;
  // Backend-sourced JSON: type-specific payloads (team_deleted has team_name; identity_claim
  // carries the full claim shape). Kept loose here and narrowed per-type below.
  payload: Record<string, unknown> | null;
  created_at: string;
}
interface NotificationsResponse {
  notifications: NotificationItem[];
}

// Returns null for types this build has no acknowledge-only copy for — such rows are skipped,
// never rendered as a blank full-screen blocker. (identity_claim is interactive, handled separately.)
function copyFor(n: NotificationItem): { title: string; body: string } | null {
  if (n.type === 'team_deleted') {
    const team = typeof n.payload?.team_name === 'string' ? n.payload.team_name : 'A team you were on';
    return {
      title: 'A team you were on was deleted',
      body: `${team} was deleted by an admin. You no longer have access to its shared conversations, briefings, or analytics. Your own captured data and personal workspace are unaffected.`,
    };
  }
  // bug-422: the decision-conflict escalation (lib/teamwork/escalate-decision-conflicts.ts) routes a
  // high-bar conflict to an accountability holder. Without this branch copyFor returned null and the
  // notification silently vanished from the inbox. The payload carries `domain` (the conflict subject);
  // resolution happens in the team brief's conflict card, so this is an acknowledge-only heads-up.
  if (n.type === 'decision_conflict_escalation') {
    const domain =
      typeof n.payload?.domain === 'string' && n.payload.domain.trim() ? n.payload.domain.trim() : 'a decision';
    return {
      title: 'A team decision conflict needs a call',
      // bug-550: the holder is deliberately NOT a party, so the conflict never appears in THEIR brief —
      // "open your team brief" was a dead pointer. The card is now viewable right here (interactive
      // branch below, backed by /conflicts/escalated/[notificationId]/card).
      body: `Two teammates landed on conflicting decisions about ${domain}, and it's been escalated to you to settle. You can review both sides here.`,
    };
  }
  // bug-422: the `blocked_on` notification is emitted when a teammate routes a conflict to you via the
  // resolution modal's "Blocked on…" verb (conflicts/resolve route). The payload carries an optional
  // free-text `context`; there are no display names, so the copy stays teammate-generic.
  if (n.type === 'blocked_on') {
    const context =
      typeof n.payload?.context === 'string' && n.payload.context.trim() ? n.payload.context.trim() : null;
    return {
      title: 'A teammate is blocked on you',
      body: context
        ? `A teammate flagged a decision conflict as blocked on you — “${context}”. Open your team brief to help unblock it.`
        : 'A teammate flagged a decision conflict as blocked on you. Open your team brief to help unblock it.',
    };
  }
  // The `ping` notification (conflicts/resolve `ping` verb) — a teammate nudged you with their own note.
  // Slack is the primary channel; this in-app copy is the FALLBACK the route writes only when Slack
  // couldn't reach you, so without this branch a Slack-less recipient would be notified nowhere.
  if (n.type === 'ping') {
    const note = typeof n.payload?.note === 'string' && n.payload.note.trim() ? n.payload.note.trim() : null;
    return {
      title: 'A teammate pinged you',
      body: note
        ? `A teammate pinged you in Terum — “${note}”. Open your team brief to take a look.`
        : 'A teammate pinged you in Terum. Open your team brief to take a look.',
    };
  }
  // The in-flow conflict alert (lib/teamwork/alert-decision-conflicts.ts). Slack is the primary channel
  // — a thread reply where the decision was posted, else a DM. This row is the TERMINAL fallback, written
  // only when Slack is unreachable (no bot token / no slack_user_id / a permanent send failure), so a
  // missing branch here dead-letters the notice for exactly the users who have no other channel. The
  // recipient is the author of the LATER decision; `payload.topic` is nullable.
  if (n.type === 'decision_conflict_inflow') {
    const topic = typeof n.payload?.topic === 'string' && n.payload.topic.trim() ? n.payload.topic.trim() : null;
    return {
      title: 'Your recent decision may conflict',
      body: topic
        ? `A decision you just made about ${topic} may contradict a standing team decision. Open your team brief to see both and settle it.`
        : 'A decision you just made may contradict a standing team decision. Open your team brief to see both and settle it.',
    };
  }
  return null;
}

/**
 * Every `user_notifications.type` the backend actively writes — the render-coverage contract.
 *
 * This list is the drift guard bug-422 asked for and #112 did not build. The same defect has now shipped
 * three times (bug-422 `decision_conflict_escalation`, bug-455 `ping`, bug-514 `decision_conflict_inflow`):
 * a new producer lands in the MVP repo, `copyFor` has no branch, the row is skipped, and the notice is
 * dead-lettered forever. Each time the fix added a per-type test, which by construction cannot fail for a
 * type nobody thought of. `pending-notification-modal.test.ts` iterates this list instead, so a type added
 * here without a render branch fails the suite.
 *
 * Deliberately excludes `teamwork_escalation`: its producer is dormant — `lib/teamwork/graph.ts` removed the
 * escalation-notify wiring because it routed off a sensitivity heuristic with no intent classifier. Add it
 * back here if that wiring is ever restored.
 */
export const BACKEND_NOTIFICATION_TYPES = [
  'team_deleted', // app/api/teams/[id]/route.ts
  'decision_conflict_escalation', // lib/teamwork/escalate-decision-conflicts.ts
  'decision_conflict_inflow', // lib/teamwork/alert-decision-conflicts.ts (Slack-unreachable fallback)
  'blocked_on', // app/api/surfaces/team/conflicts/[edgeKey]/resolve/route.ts
  'ping', // app/api/surfaces/team/conflicts/[edgeKey]/resolve/route.ts
  IDENTITY_CLAIM_NOTIFICATION_TYPE, // lib/teamwork/identity-claim-scorer.ts (interactive branch)
] as const;

/** Does this build render `type` at all — acknowledge-only copy or the interactive claim branch? */
export function hasRenderBranch(type: string): boolean {
  return copyFor({ id: '', type, payload: {}, created_at: '' }) !== null || type === IDENTITY_CLAIM_NOTIFICATION_TYPE;
}

/**
 * Parse an `identity_claim` notification's payload with the shared contract. Returns the validated
 * payload, or null for any non-claim type / malformed payload — a malformed row is skipped, never
 * rendered as a blank blocker. (Layer 0 Path B; the producing scorer is dormant, so this only fires
 * once real claim notifications exist.)
 */
export function parseIdentityClaim(n: { type: string; payload: unknown }): IdentityClaimPayload | null {
  if (n.type !== IDENTITY_CLAIM_NOTIFICATION_TYPE) return null;
  const result = IdentityClaimPayloadSchema.safeParse(n.payload);
  return result.success ? result.data : null;
}

/** Human-readable name for the unclaimed contributor the claim suggests is the user. */
export function claimCandidateName(payload: IdentityClaimPayload): string {
  const name = payload.evidence?.display_name?.trim();
  return name && name.length > 0 ? name : 'this contributor';
}

/** The evidence lines shown under the "is this you?" prompt — signals plus the shared-count rollups. */
export function claimEvidenceLines(payload: IdentityClaimPayload): string[] {
  const evidence = payload.evidence;
  const lines = [...(evidence?.signals ?? [])];
  const collaborators = evidence?.shared_collaborator_count;
  if (typeof collaborators === 'number' && collaborators > 0) {
    lines.push(`${collaborators} shared collaborator${collaborators === 1 ? '' : 's'}`);
  }
  const channels = evidence?.shared_channel_count;
  if (typeof channels === 'number' && channels > 0) {
    lines.push(`${channels} shared channel${channels === 1 ? '' : 's'}`);
  }
  return lines;
}

export function PendingNotificationModal() {
  const queryClient = useQueryClient();
  // Acknowledged-this-session ids, so a failing/slow dismiss POST can never trap the
  // user behind the full-screen overlay (the server dismiss is best-effort below).
  const [acknowledged, setAcknowledged] = useState<Set<string>>(new Set());
  // bug-550: which escalation notification (if any) the holder is currently viewing the card for.
  const [viewingEscalationId, setViewingEscalationId] = useState<string | null>(null);

  const { data } = useQuery<NotificationsResponse>({
    queryKey: ['notifications'],
    queryFn: () => apiFetch<NotificationsResponse>('/api/surfaces/notifications'),
  });
  const dismiss = useMutation({
    mutationFn: (id: string) =>
      apiFetch('/api/surfaces/notifications/dismiss', {
        method: 'POST',
        body: JSON.stringify({ id }),
      }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['notifications'] }),
    // Best-effort: if the server dismiss fails the row simply resurfaces on a later
    // load — the modal is already hidden locally, so the user is never locked out.
    onError: (err) => console.warn('[notifications] dismiss failed; will resurface next load', err),
  });

  // Confirming an identity claim merges the unclaimed ghost into the caller's verified person.
  // The route dismisses the notification server-side on success; we mirror that locally + refresh
  // the teamwork-graph caches the merge changes. Errors are surfaced inline (no silent failure).
  const claim = useMutation({
    mutationFn: (candidatePersonId: string) =>
      apiFetch('/api/surfaces/identity-claim', {
        method: 'POST',
        body: JSON.stringify({ candidate_person_id: candidatePersonId }),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
      queryClient.invalidateQueries({ queryKey: ['surfaces', 'team-map'] });
      queryClient.invalidateQueries({ queryKey: ['surfaces', 'team-members'] });
    },
  });

  // First notification that this build can render AND the user hasn't acknowledged
  // this session. Oldest first (server orders ascending).
  const current = data?.notifications?.find(
    (n) => !acknowledged.has(n.id) && (copyFor(n) !== null || parseIdentityClaim(n) !== null),
  );
  if (!current) return null;

  const acknowledge = (id: string) => {
    // Optimistic, local-first: hide immediately, then fire the server dismiss.
    setAcknowledged((prev) => new Set(prev).add(id));
    dismiss.mutate(id);
  };

  // --- identity_claim: interactive "is this you?" prompt -------------------------------------
  const claimPayload = parseIdentityClaim(current);
  if (claimPayload) {
    const candidateName = claimCandidateName(claimPayload);
    const evidenceLines = claimEvidenceLines(claimPayload);
    const confirmClaim = () => {
      claim.mutate(claimPayload.candidate_person_id, {
        onSuccess: () => setAcknowledged((prev) => new Set(prev).add(current.id)),
      });
    };
    return (
      <div className="ext-modal-overlay">
        <div className="ext-modal" onClick={(e) => e.stopPropagation()}>
          <p className="ext-modal-badge">IS THIS YOU?</p>
          <h2 className="ext-modal-title">We matched some activity to you</h2>
          <p className="ext-modal-desc">
            We found a contributor — <strong>{candidateName}</strong> — whose work looks like yours.
            Confirm it&rsquo;s you and we&rsquo;ll link that activity into your profile so your
            contributions stay in one place. This only affects how your team graph is grouped.
          </p>
          {evidenceLines.length > 0 && (
            <div className="ext-modal-steps">
              <ol>
                {evidenceLines.map((line, i) => (
                  <li key={i}>
                    <span>•</span>
                    {line}
                  </li>
                ))}
              </ol>
            </div>
          )}
          {claim.isError && (
            <p className="ext-modal-error">
              Couldn&rsquo;t confirm right now. Please try again — nothing was changed.
            </p>
          )}
          <div className="ext-modal-actions">
            <button
              className="ext-modal-cta"
              onClick={confirmClaim}
              disabled={claim.isPending}
              style={{ border: 'none', cursor: 'pointer' }}
            >
              {claim.isPending ? 'Confirming…' : 'Yes, this is me'}
            </button>
            <button
              className="ext-modal-cta ext-modal-cta-secondary"
              onClick={() => acknowledge(current.id)}
              disabled={claim.isPending}
              style={{ cursor: 'pointer' }}
            >
              Not me
            </button>
          </div>
        </div>
      </div>
    );
  }

  // --- decision_conflict_escalation: interactive "review both sides" prompt (bug-550) ---------
  // The holder is a NON-party (self-escalation is skipped server-side), so the conflict never appears
  // in their own brief and the party card route 403s them. The escalated card route authorizes by this
  // very notification (ownership + membership + a read-time entitlement re-check), so "View the
  // conflict" is the one path a holder has into the material. Dismiss stays available either way.
  if (current.type === 'decision_conflict_escalation') {
    const copy = copyFor(current);
    if (!copy) return null;
    if (viewingEscalationId === current.id) {
      return (
        <EscalatedConflictCardView
          notificationId={current.id}
          onBack={() => setViewingEscalationId(null)}
          onDismiss={() => {
            setViewingEscalationId(null);
            acknowledge(current.id);
          }}
        />
      );
    }
    return (
      <div className="ext-modal-overlay">
        <div className="ext-modal" onClick={(e) => e.stopPropagation()}>
          <h2 className="ext-modal-title">{copy.title}</h2>
          <p className="ext-modal-desc">{copy.body}</p>
          <div className="ext-modal-actions">
            <button
              className="ext-modal-cta"
              onClick={() => setViewingEscalationId(current.id)}
              style={{ border: 'none', cursor: 'pointer' }}
            >
              View the conflict
            </button>
            <button
              className="ext-modal-cta ext-modal-cta-secondary"
              onClick={() => acknowledge(current.id)}
              style={{ cursor: 'pointer' }}
            >
              Dismiss
            </button>
          </div>
        </div>
      </div>
    );
  }

  // --- acknowledge-only notifications (e.g. team_deleted) ------------------------------------
  const copy = copyFor(current);
  if (!copy) return null;
  return (
    // No overlay-click dismiss: require explicit acknowledgement so the user reads it.
    <div className="ext-modal-overlay">
      <div className="ext-modal" onClick={(e) => e.stopPropagation()}>
        <h2 className="ext-modal-title">{copy.title}</h2>
        <p className="ext-modal-desc">{copy.body}</p>
        <button
          className="ext-modal-cta"
          onClick={() => acknowledge(current.id)}
          style={{ display: 'inline-block', border: 'none', cursor: 'pointer' }}
        >
          Got it
        </button>
      </div>
    </div>
  );
}

/**
 * Read-only escalated-conflict card (bug-550). Fetches the same card shape the party modal shows, via
 * the holder-authorized route — no resolve verbs (the resolve route requires party ownership; the
 * holder's lever is talking to the parties, which is the escalation's whole point). A 404 here is a
 * real state, not an error: the conflict was resolved (escalations are deleted on resolve) or its
 * sources were made private since the escalation fired.
 */
function EscalatedConflictCardView({
  notificationId,
  onBack,
  onDismiss,
}: {
  notificationId: string;
  onBack: () => void;
  onDismiss: () => void;
}) {
  const card = useQuery({
    queryKey: ['escalated-conflict-card', notificationId],
    queryFn: () =>
      apiFetch<ConflictCardResponse>(`/api/surfaces/team/conflicts/escalated/${notificationId}/card`, {
        method: 'POST',
      }),
    staleTime: 5 * 60 * 1000,
    retry: false,
  });

  return (
    <div className="ext-modal-overlay">
      <div className="ext-modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 560 }}>
        {card.isPending && <p className="ext-modal-desc">Loading the conflict…</p>}
        {card.isError && (
          <>
            <h2 className="ext-modal-title">This conflict is no longer viewable</h2>
            <p className="ext-modal-desc">
              It may already have been resolved by the people involved, or its source conversations were
              made private. Nothing needs your attention here anymore.
            </p>
          </>
        )}
        {card.data && (
          <>
            <p className="ext-modal-badge">ESCALATED TO YOU</p>
            <h2 className="ext-modal-title">{card.data.headline ?? card.data.topic ?? 'Two conflicting decisions'}</h2>
            {card.data.standfirst && <p className="ext-modal-desc">{card.data.standfirst}</p>}
            <div className="ext-modal-steps">
              <ol>
                {card.data.options.map((o) => (
                  <li key={o.decisionId}>
                    <span>•</span>
                    <div>
                      <div>{o.decisionText}</div>
                      {o.judgment && <div style={{ opacity: 0.8, marginTop: 2 }}>{o.judgment}</div>}
                      <div style={{ opacity: 0.6, marginTop: 2, fontSize: 12 }}>
                        {[o.author, o.source ? siteLabel(o.source) : null, o.decidedAt ? formatSourceDate(o.decidedAt) : null]
                          .filter(Boolean)
                          .join(' · ')}
                      </div>
                    </div>
                  </li>
                ))}
              </ol>
            </div>
            {card.data.consequence && <p className="ext-modal-desc">{card.data.consequence}</p>}
            <p className="ext-modal-desc" style={{ opacity: 0.75 }}>
              You were routed this because it sits near your domain. Settling it happens with the two
              people involved — this view gives you both sides.
            </p>
          </>
        )}
        <div className="ext-modal-actions">
          <button className="ext-modal-cta ext-modal-cta-secondary" onClick={onBack} style={{ cursor: 'pointer' }}>
            Back
          </button>
          <button className="ext-modal-cta" onClick={onDismiss} style={{ border: 'none', cursor: 'pointer' }}>
            Dismiss notification
          </button>
        </div>
      </div>
    </div>
  );
}
